import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { func } from 'prop-types'
import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { Row, Col } from '@gympass/yoga'
import sendSegmentEvent from '@client/utils/segmentHelper'
import useContact from '@hooks/useContact'
import useLoading from '@hooks/useLoading'
import useDynamicFields from '@hooks/useDynamicFields'
import useInternationalization from '@hooks/useInternationalization'
import useQuery from '@hooks/useQuery'

import DynamicForm from '@components/DynamicForm'
import LoadingButton from '@components/LoadingButton'

import useValidation from '@hooks/useFormValidation'

import * as Style from './Form.style'

export default function Form({ onSubmit, setFunnelStage }) {
  const isFromEmailFlow = useQuery().get('email-flow')

  const { states } = useSelector(store => store.country)

  const { fields: companyInfoFields } = useDynamicFields('companyInfo')

  const { country } = useInternationalization()

  const [disabledField, setDisabledField] = useState({})

  const { contact } = useContact()
  const { isLoading } = useLoading()
  const validationSchema = useValidation(companyInfoFields)

  const {
    register,
    errors,
    handleSubmit,
    control,
    setValue,
    clearError,
    getValues,
  } = useForm({
    defaultValues: { ...contact },
    validationSchema,
  })

  const companyInfoFieldsWithItems = companyInfoFields.map(field => {
    return field.id === 'state'
      ? {
          ...field,
          items: {
            defaultElements: {
              elements: states,
            },
          },
        }
      : field
  })

  const handlePrivacyClick = () => {
    sendSegmentEvent('page_clicked', {
      click_cta: 'privacy_policy',
      component_category: 'quote',
      component_position: 'unique',
      country_name: country.toUpperCase(),
      event_category: 'unlogged_enterprise',
      page_name: 'company_info',
    })
  }

  useEffect(() => {
    sendSegmentEvent('page_viewed', {
      country_name: 'US',
      event_category: 'unlogged_enterprise',
      page_name: 'company_info',
    })
  }, [])

  useEffect(() => {
    if (contact) {
      setDisabledField({
        email: !isFromEmailFlow && !!contact.email,
      })
    }
  }, [contact && contact.email])

  useEffect(() => {
    setDisabledField({
      ...disabledField,
      email: !isFromEmailFlow && !!contact.email,
    })
  }, [])

  useEffect(() => {
    if (contact && contact.email) {
      setValue('email', contact.email)
    }
  }, [setValue])

  return (
    <Style.FormWrapper id="company-info-form">
      <DynamicForm
        fields={companyInfoFieldsWithItems}
        register={register}
        disabledFields={disabledField}
        errors={errors}
        control={control}
        setValue={setValue}
        clearErrors={clearError}
        getValues={getValues}
        setFunnelStage={setFunnelStage}
      />

      <Row>
        <Col xxs={12}>
          <section>
            <Style.PrivacyPolicy>
              <FormattedMessage
                id="smb_acquisition_company_info.page.lead_capture.warnings.privacy_policy"
                values={{
                  a: (...chunks) => (
                    <a
                      href={`https://www.gympass.com/${country}/privacy`}
                      onClick={handlePrivacyClick}
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </Style.PrivacyPolicy>
          </section>
        </Col>
      </Row>

      <Row>
        <Col xxs={12} md-start={5} md={4} xl-start={4} xl={6}>
          <Style.ButtonWrapper>
            <LoadingButton
              full
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              <Style.ButtonText>
                <FormattedMessage id="smb_acquisition_company_info.page.lead_capture.call_to_action" />
              </Style.ButtonText>
            </LoadingButton>
          </Style.ButtonWrapper>
        </Col>
      </Row>
    </Style.FormWrapper>
  )
}

Form.propTypes = {
  onSubmit: func.isRequired,
  setFunnelStage: func.isRequired,
}
